import React, { useState } from 'react';
import {
  Mail,
  Clock,
  ShieldAlert,
  MessageSquare,
  Building2,
  MapPin,
  Send,
  HelpCircle,
  CreditCard,
  Lock,
  Handshake,
  CheckCircle2,
  Sparkles,
  ExternalLink,
  ChevronRight,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';
import { submitContactInquiry } from '../Service/ContactService';
import { getEmailValidationError } from '../../Shared/Utils/validationPolicy';
import { useValidationPolicy } from '../../Shared/Hooks/useValidationPolicy';

interface ContactCategory {
  title: string;
  email: string;
  description: string;
  icon: React.ElementType;
}

const contactCategories: ContactCategory[] = [
  {
    title: 'General Support',
    email: 'support@nexgent.tech',
    description: 'Platform assistance, account setup, candidate/recruiter portal navigation.',
    icon: HelpCircle,
  },
  {
    title: 'Privacy & Data Requests',
    email: 'privacy@nexgent.tech',
    description: 'Data access, account deletion requests, DPDP Act compliance, and profile privacy.',
    icon: Lock,
  },
  {
    title: 'Billing & Subscriptions',
    email: 'billing@nexgent.tech',
    description: 'Queries on Ignite, Accelerate, Dominate plans, credit allocation, or tax invoices.',
    icon: CreditCard,
  },
  {
    title: 'Partnerships & Corporate',
    email: 'partners@nexgent.tech',
    description: 'Enterprise search integration, agency partnerships, and strategic alliances.',
    icon: Handshake,
  },
];

const responseSLAs = [
  { type: 'General Support', sla: 'Within 24–48 Business Hours' },
  { type: 'Technical Issues', sla: 'Within 24 Business Hours' },
  { type: 'Billing Queries', sla: 'Within 2 Business Days' },
  { type: 'Privacy & Data Requests', sla: 'Within 7 Business Days' },
];

export const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'Candidate',
    subject: 'General Support',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const { policy } = useValidationPolicy();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const emailError = getEmailValidationError(formData.email, policy);
    if (emailError) {
      setSubmitError(emailError);
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    const response = await submitContactInquiry({
      name: formData.name.trim(),
      email: formData.email.trim(),
      subject: formData.subject,
      message: formData.message.trim(),
      profileRole: formData.role,
    });

    setSubmitting(false);

    if (response.success) {
      setFormData({
        name: '',
        email: '',
        role: 'Candidate',
        subject: 'General Support',
        message: '',
      });
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
    } else {
      setSubmitError(response.message || 'Could not send your message. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Background Cyber Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                NEXGENT SUPPORT CENTER
              </span>
              <span className="text-xs text-slate-500">Motherson Infotech Division</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              Get in Touch
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <Clock className="w-4 h-4 text-cyan-400" />
            <span>Support Active • IST (UTC+5:30)</span>
          </div>
        </div>
      </header>

      {/* Main Container Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
        
        {/* Intro Grid: Hero + Quick Form */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Column: Platform Overview & Address */}
          <div className="lg:col-span-6 space-y-6">
            <div className="bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80 space-y-4">
              <div className="flex items-center gap-2 text-cyan-400 text-xs font-mono font-semibold uppercase tracking-wider">
                <Sparkles className="w-4 h-4" /> Dedicated Professional Support
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-white">
                We're here to assist candidates, recruiters, and enterprise partners.
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                Nexgent is a private leadership hiring platform connecting experienced professionals with verified recruiters through a secure, privacy-focused recruitment ecosystem. Reach out with queries regarding your account, subscription tiers, privacy controls, or technical issues.
              </p>
            </div>

            {/* Business Address & Hours Card */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-slate-900/40 p-5 rounded-2xl border border-slate-800/80 space-y-3">
                <div className="flex items-center gap-2 text-white font-semibold text-sm">
                  <Building2 className="w-4 h-4 text-cyan-400" /> Business Address
                </div>
                <div className="text-xs text-slate-400 space-y-1">
                  <p className="font-bold text-white">Nexgent</p>
                  <p className="text-cyan-400 font-mono text-[11px]">(Operated by Motherson Infotech)</p>
                  <p className="text-slate-500 italic pt-1">Business Address will be updated soon.</p>
                  <p className="flex items-center gap-1 text-slate-300 pt-1">
                    <MapPin className="w-3.5 h-3.5 text-cyan-400" /> Pune, Maharashtra, India
                  </p>
                </div>
              </div>

              <div className="bg-slate-900/40 p-5 rounded-2xl border border-slate-800/80 space-y-3">
                <div className="flex items-center gap-2 text-white font-semibold text-sm">
                  <Clock className="w-4 h-4 text-cyan-400" /> Support Hours
                </div>
                <div className="text-xs text-slate-400 space-y-2">
                  <div>
                    <span className="text-white font-medium block">Mon – Fri</span>
                    <span className="font-mono text-cyan-300">9:00 AM – 6:00 PM IST</span>
                  </div>
                  <div>
                    <span className="text-white font-medium block">Saturday</span>
                    <span className="font-mono text-cyan-300">10:00 AM – 2:00 PM IST</span>
                  </div>
                  <div className="text-slate-500 text-[11px] font-mono">
                    Sunday & Public Holidays: Closed
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Direct Contact Form */}
          <div className="lg:col-span-6 bg-slate-900/60 p-6 md:p-8 rounded-2xl border border-slate-800/80 backdrop-blur-md shadow-xl relative">
            <h3 className="text-lg font-bold text-white mb-1">Send Support Inquiry</h3>
            <p className="text-xs text-slate-400 mb-6">Fill out the details below and our team will route your request to the appropriate department.</p>

            {submitted && (
              <div className="mb-6 p-4 bg-cyan-950/80 border border-cyan-800 text-cyan-300 rounded-xl text-xs flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-cyan-400 shrink-0" />
                <span>Your message has been received! A support representative will respond shortly.</span>
              </div>
            )}

            {submitError && (
              <div className="mb-6 p-4 bg-rose-950/80 border border-rose-800 text-rose-300 rounded-xl text-xs flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-rose-400 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-slate-400 font-medium">Full Name</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Swapnil Kamble"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-slate-400 font-medium">Email Address</label>
                  <input
                    type="email"
                    required
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-slate-400 font-medium">User Profile Type</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                  >
                    <option value="Candidate">Leadership Candidate</option>
                    <option value="Recruiter">Corporate Recruiter</option>
                    <option value="Partner">Enterprise Partner</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-slate-400 font-medium">Category</label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                  >
                    <option value="General Support">General Support</option>
                    <option value="Billing & Subscription">Billing & Subscriptions</option>
                    <option value="Privacy & Data">Privacy & Data Requests</option>
                    <option value="Partnership">Business / Partnerships</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-slate-400 font-medium">Message Details</label>
                <textarea
                  rows={4}
                  required
                  placeholder="Describe your issue or query..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500 transition-colors"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold py-3 rounded-lg transition-all shadow-[0_0_20px_rgba(6,182,212,0.3)] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Submit Inquiry
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Section 2: Channel Cards */}
        <section className="space-y-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-cyan-400" /> Direct Department Emails
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactCategories.map((cat, idx) => {
              const Icon = cat.icon;
              return (
                <div key={idx} className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 space-y-3 hover:border-slate-700 transition-all">
                  <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg w-fit border border-slate-700">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm">{cat.title}</h3>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">{cat.description}</p>
                  </div>
                  <a
                    href={`mailto:${cat.email}`}
                    className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold text-cyan-400 hover:text-cyan-300 pt-2 transition-colors"
                  >
                    {cat.email} <ChevronRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 3: Expected Response Times Table */}
        <section className="bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-bold text-white">Expected Response Times (SLAs)</h2>
              <p className="text-xs text-slate-400">Response timelines measured during standard operating business hours</p>
            </div>
            <span className="text-[11px] font-mono bg-slate-950 text-slate-400 border border-slate-800 px-3 py-1 rounded-md w-fit">
              Excludes Public Holidays
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 font-mono">
                  <th className="py-3 px-4 font-medium">Request Type</th>
                  <th className="py-3 px-4 font-medium">Expected Response Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-slate-300">
                {responseSLAs.map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-900/60 transition-colors">
                    <td className="py-3 px-4 font-medium text-white">{row.type}</td>
                    <td className="py-3 px-4 font-mono text-cyan-400">{row.sla}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 4: Security Concerns & Feedback Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Security Alert Block */}
          <div className="bg-red-950/20 border border-red-900/40 rounded-2xl p-6 space-y-3">
            <div className="flex items-center gap-2.5 text-red-400 font-bold text-sm">
              <ShieldAlert className="w-5 h-5 shrink-0" /> Report Security Concerns
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              If you discover a security vulnerability, suspicious platform behavior, or believe your account credentials have been compromised, notify our engineering desk immediately.
            </p>
            <div className="pt-1">
              <a
                href="mailto:security@nexgent.tech"
                className="inline-flex items-center gap-2 text-xs font-mono font-bold bg-red-950 text-red-300 border border-red-800/80 px-3.5 py-2 rounded-lg hover:bg-red-900/50 transition-colors"
              >
                security@nexgent.tech
              </a>
            </div>
          </div>

          {/* Feedback & Suggestions Block */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 space-y-3">
            <div className="flex items-center gap-2.5 text-cyan-400 font-bold text-sm">
              <MessageSquare className="w-5 h-5 shrink-0" /> Feedback & Feature Suggestions
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              We continuously refine Nexgent based on recommendations from our executive community. Feature requests and UX suggestions can be directed to:
            </p>
            <div className="pt-1">
              <a
                href="mailto:feedback@nexgent.tech"
                className="inline-flex items-center gap-2 text-xs font-mono font-bold bg-cyan-950 text-cyan-300 border border-cyan-800/80 px-3.5 py-2 rounded-lg hover:bg-cyan-900/50 transition-colors"
              >
                feedback@nexgent.tech
              </a>
            </div>
          </div>

        </div>

        {/* Footer Bar */}
        <footer className="p-4 bg-slate-900/80 rounded-xl border border-slate-800 text-center text-xs text-cyan-500 font-mono">
          {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent. All rights reserved. */}
          Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First

        </footer>

      </main>
      <LandingFooter/>
    </div>
  );
};

export default ContactUs;